import type { InsightResult, InsightStatus } from '../models/insight.types'
import type { InsightListParams, InsightStatusResponse } from '../models/insightApi.types'
import { normalizePaginatedResponse } from '../service/normalizeListResponse'

import { cleanQueryParams } from './cleanQueryParams'

import { appHttp, unwrap, type Paginated } from '@/shared/http'

function requestConfig(params: object, signal?: AbortSignal) {
  return {
    params: cleanQueryParams(params),
    suppressGlobalErrorHandler: true,
    meta: { suppressGlobalErrorHandler: true },
    ...(signal ? { signal } : {}),
  }
}

/** Workspace consumption transport. All three responses are flat in the published backend. */
export class InsightApi {
  constructor(private readonly http = appHttp) {}

  async list(
    accountId: number,
    workspaceId: number,
    params: InsightListParams = {},
    signal?: AbortSignal,
  ): Promise<Paginated<InsightResult>> {
    const response = await this.http.get(
      `/accounts/${accountId}/workspaces/${workspaceId}/insights/`,
      requestConfig(params, signal),
    )
    return normalizePaginatedResponse<InsightResult>(response.data, params)
  }

  async get(
    accountId: number,
    workspaceId: number,
    insightId: number,
    tab?: string,
    signal?: AbortSignal,
  ): Promise<InsightResult> {
    const response = await this.http.get(
      `/accounts/${accountId}/workspaces/${workspaceId}/insights/${insightId}/`,
      requestConfig({ tab }, signal),
    )
    return unwrap<InsightResult>(response)
  }

  async updateStatus(
    accountId: number,
    workspaceId: number,
    insightId: number,
    status: InsightStatus,
    signal?: AbortSignal,
  ): Promise<InsightStatusResponse> {
    const response = await this.http.post(
      `/accounts/${accountId}/workspaces/${workspaceId}/insights/${insightId}/status/`,
      { status },
      requestConfig({}, signal),
    )
    return unwrap<InsightStatusResponse>(response)
  }
}

export const insightApi = new InsightApi()
